import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Heading,
  Text,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { useWeb3 } from '../lib/Web3Provider';
import { extractErrorMessage } from '../lib/utils';

interface TokenConfigFormProps {
  initialTokenId?: string;
}

const TokenConfigForm: React.FC<TokenConfigFormProps> = ({ initialTokenId = '' }) => {
  const { readContract, writeContract } = useWeb3();
  const [tokenId, setTokenId] = useState(initialTokenId);
  const [fetchingToken, setFetchingToken] = useState(false);
  const [tokenExists, setTokenExists] = useState(false);
  const [tokenURI, setTokenURI] = useState('');
  const [maxSupply, setMaxSupply] = useState(0);
  const [maxPerUser, setMaxPerUser] = useState(0);
  const [metadataName, setMetadataName] = useState('');
  const [metadataDescription, setMetadataDescription] = useState('');
  const [metadataImageURL, setMetadataImageURL] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(false);
  const toast = useToast();

  const fetchTokenDetails = async () => {
    if (!readContract || !tokenId) return;

    setFetchingToken(true);
    setTokenExists(false);
    
    try {
      // Check if token exists
      const exists = await readContract.exists(tokenId);
      setTokenExists(exists);

      if (exists) {
        // Get current URI
        const uri = await readContract.uri(tokenId);
        setTokenURI(uri);

        // Get max supply
        const max = await readContract.getMax(tokenId);
        setMaxSupply(Number(max));

        // Get max per user
        const perUserMax = await readContract.getPerUserMax(tokenId);
        setMaxPerUser(Number(perUserMax));

        // Try to fetch metadata if URI is set
        if (uri && uri.startsWith('ipfs://')) {
          try {
            const ipfsUrl = uri.replace('ipfs://', 'https://freeweb3.infura-ipfs.io/ipfs/');
            const response = await fetch(ipfsUrl);
            if (response.ok) {
              const metadata = await response.json();
              setMetadataName(metadata.name || '');
              setMetadataDescription(metadata.description || '');
              setMetadataImageURL(metadata.image || '');
            }
          } catch (error) {
            console.warn('Failed to fetch metadata:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching token details:', error);
      toast({
        title: 'Error',
        description: extractErrorMessage(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFetchingToken(false);
    }
  };

  const handleSubmitURI = async () => {
    if (!writeContract || !tokenId) return;

    setSaving(true);
    try {
      // Set the URI
      const tx = await writeContract.setURI(tokenId, tokenURI);
      await tx.wait();
      
      setActionSuccess(true);
      toast({
        title: 'Success',
        description: 'Token URI updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating URI:', error);
      toast({
        title: 'Error',
        description: extractErrorMessage(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitMaxSupply = async () => {
    if (!writeContract || !tokenId) return;

    setSaving(true);
    try {
      // Set max supply
      const tx = await writeContract.setMax(tokenId, maxSupply);
      await tx.wait();
      
      setActionSuccess(true);
      toast({
        title: 'Success',
        description: 'Maximum supply updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating max supply:', error);
      toast({
        title: 'Error',
        description: extractErrorMessage(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitMaxPerUser = async () => {
    if (!writeContract || !tokenId) return;

    setSaving(true);
    try {
      // Set max per user
      const tx = await writeContract.setPerUserMax(tokenId, maxPerUser);
      await tx.wait();
      
      setActionSuccess(true);
      toast({
        title: 'Success',
        description: 'Maximum per user updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating max per user:', error);
      toast({
        title: 'Error',
        description: extractErrorMessage(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (tokenId) {
      fetchTokenDetails();
    }
  }, [tokenId, readContract]);

  return (
    <Box>
      <Heading as="h2" size="lg" mb={5}>
        Configure Token
      </Heading>
      
      <Text mb={6}>
        Set token URI metadata and supply limits
      </Text>
      
      <FormControl mb={6}>
        <FormLabel>Token ID</FormLabel>
        <Flex>
          <Input
            placeholder="Enter the token ID to configure"
            value={tokenId}
            onChange={(e) => setTokenId(e.target.value)}
            mr={2}
          />
          <Button
            onClick={fetchTokenDetails}
            isLoading={fetchingToken}
            loadingText="Checking..."
          >
            Look Up
          </Button>
        </Flex>
      </FormControl>
      
      {fetchingToken && (
        <Flex justify="center" my={8}>
          <Spinner size="xl" />
        </Flex>
      )}
      
      {!fetchingToken && tokenId && !tokenExists && (
        <Alert status="warning" mb={6}>
          <AlertIcon />
          Token ID {tokenId} does not exist or you don't have permission to configure it.
        </Alert>
      )}
      
      {!fetchingToken && tokenExists && (
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>Token URI</Tab>
            <Tab>Supply Limits</Tab>
          </TabList>
          
          <TabPanels>
            <TabPanel>
              <Box mb={6}>
                <Heading as="h3" size="md" mb={4}>
                  Token URI
                </Heading>
                
                <FormControl mb={4}>
                  <FormLabel>URI</FormLabel>
                  <Input
                    placeholder="ipfs://..."
                    value={tokenURI}
                    onChange={(e) => setTokenURI(e.target.value)}
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Usually an IPFS link to the token metadata
                  </Text>
                </FormControl>
                
                <Box p={4} borderWidth="1px" borderRadius="md" mb={4}>
                  <Heading as="h4" size="sm" mb={3}>
                    Preview Metadata
                  </Heading>
                  
                  <FormControl mb={3} isDisabled>
                    <FormLabel>Name</FormLabel>
                    <Input value={metadataName} readOnly />
                  </FormControl>
                  
                  <FormControl mb={3} isDisabled>
                    <FormLabel>Description</FormLabel>
                    <Textarea value={metadataDescription} readOnly />
                  </FormControl>
                  
                  <FormControl isDisabled>
                    <FormLabel>Image URL</FormLabel>
                    <Input value={metadataImageURL} readOnly />
                  </FormControl>
                </Box>
                
                <Flex justify="center" mt={6}>
                  <Button
                    colorScheme="blue"
                    isLoading={saving}
                    loadingText="Saving..."
                    onClick={handleSubmitURI}
                  >
                    Update URI
                  </Button>
                </Flex>
              </Box>
            </TabPanel>
            
            <TabPanel>
              <Box mb={6}>
                <Heading as="h3" size="md" mb={4}>
                  Supply Limits
                </Heading>
                
                <FormControl mb={6}>
                  <FormLabel>Maximum Total Supply</FormLabel>
                  <NumberInput
                    min={-1}
                    value={maxSupply}
                    onChange={(_, valueAsNumber) => setMaxSupply(valueAsNumber)}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Set to -1 for unlimited supply
                  </Text>
                  
                  <Flex justify="flex-end" mt={2}>
                    <Button
                      colorScheme="blue"
                      size="sm"
                      isLoading={saving}
                      loadingText="Saving..."
                      onClick={handleSubmitMaxSupply}
                    >
                      Update Total Limit
                    </Button>
                  </Flex>
                </FormControl>
                
                <FormControl mb={4}>
                  <FormLabel>Maximum Per User</FormLabel>
                  <NumberInput
                    min={-1}
                    value={maxPerUser}
                    onChange={(_, valueAsNumber) => setMaxPerUser(valueAsNumber)}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Set to -1 for unlimited tokens per user
                  </Text>
                  
                  <Flex justify="flex-end" mt={2}>
                    <Button
                      colorScheme="blue"
                      size="sm"
                      isLoading={saving}
                      loadingText="Saving..."
                      onClick={handleSubmitMaxPerUser}
                    >
                      Update Per-User Limit
                    </Button>
                  </Flex>
                </FormControl>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
      
      {actionSuccess && (
        <Alert status="success" mt={6}>
          <AlertIcon />
          Token configuration updated successfully!
        </Alert>
      )}
    </Box>
  );
};

export default TokenConfigForm;